import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { User as UserIcon, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { client } from "@/utils/orpc";

export const Route = createFileRoute("/users/")({
	component: UsersPage,
});

type User = {
	id: string;
	name: string;
	email: string;
	image: string | null;
	emailVerified: boolean;
	createdAt: Date;
	updatedAt: Date;
	organizationCount: number;
	projectCount: number;
};

type ClientWithUsers = typeof client & {
	admin: typeof client.admin & {
		users: typeof client.admin.users & {
			listUsers: (input: {
				search?: string;
				limit?: number;
				offset?: number;
			}) => Promise<{
				users: User[];
				total: number;
				limit: number;
				offset: number;
			}>;
		};
	};
};

function UsersPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [search, setSearch] = useState("");
	const [debouncedSearch, setDebouncedSearch] = useState("");
	const [selectedUser, setSelectedUser] = useState<User | null>(null);
	const [showDetailsPanelOnMobile, setShowDetailsPanelOnMobile] =
		useState(false);

	// Debounce search input
	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearch(search);
		}, 300);

		return () => clearTimeout(timer);
	}, [search]);

	// Fetch users
	const queryKey = [
		"admin",
		"users",
		"list",
		{ search: debouncedSearch, limit: 50, offset: 0 },
	];

	const { data, isLoading } = useQuery({
		queryKey,
		queryFn: async () => {
			const result = await (client as ClientWithUsers).admin.users.listUsers({
				search: debouncedSearch || undefined,
				limit: 50,
				offset: 0,
			});
			return result;
		},
		staleTime: 5 * 60 * 1000, // 5 minutes
		gcTime: 10 * 60 * 1000, // 10 minutes
	});

	const navigateToDetails = (userId: string) => {
		navigate({ to: `/users/${userId}` });
	};

	return (
		<div className="space-y-6">
			<div>
				<h1 className="font-bold text-3xl">{t("admin:users.title")}</h1>
				<p className="text-muted-foreground">{t("admin:users.subtitle")}</p>
			</div>

			<div className="flex h-[calc(100vh-12rem)] gap-4 overflow-hidden rounded-lg border bg-card shadow-sm">
				{/* Main content area */}
				<div
					className={`flex-1 overflow-hidden ${showDetailsPanelOnMobile ? "hidden md:block" : ""}`}
				>
					<div className="border-b p-4">
						<Input
							placeholder={t("admin:users.search_placeholder")}
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							className="max-w-md"
						/>
					</div>

					<ScrollArea className="h-[calc(100%-5rem)]">
						<div className="p-4">
							{isLoading ? (
								<div className="space-y-3">
									{Array.from({ length: 5 }, (_, i) => `skeleton-${i}`).map(
										(id) => (
											<Skeleton key={id} className="h-16 w-full" />
										),
									)}
								</div>
							) : data && data.users.length > 0 ? (
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead>{t("admin:users.name")}</TableHead>
											<TableHead>{t("admin:users.email")}</TableHead>
											<TableHead>{t("admin:users.organizations")}</TableHead>
											<TableHead>{t("admin:users.projects")}</TableHead>
											<TableHead>{t("admin:users.status")}</TableHead>
											<TableHead>{t("admin:users.created")}</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{data.users.map((user: User) => (
											<TableRow
												key={user.id}
												onClick={() => {
													setSelectedUser(user);
													setShowDetailsPanelOnMobile(true);
												}}
												onDoubleClick={() => navigateToDetails(user.id)}
												className={`cursor-pointer ${selectedUser?.id === user.id ? "bg-muted" : ""}`}
											>
												<TableCell>
													<button
														type="button"
														onClick={(e) => {
															e.stopPropagation();
															navigateToDetails(user.id);
														}}
														className="flex items-center gap-3 text-left"
													>
														<Avatar className="h-8 w-8">
															<AvatarImage src={user.image || undefined} />
															<AvatarFallback>
																{user.name.substring(0, 2).toUpperCase()}
															</AvatarFallback>
														</Avatar>
														<div className="font-medium hover:underline">
															{user.name}
														</div>
													</button>
												</TableCell>
												<TableCell className="font-mono text-sm">
													{user.email}
												</TableCell>
												<TableCell>{user.organizationCount}</TableCell>
												<TableCell>{user.projectCount}</TableCell>
												<TableCell>
													{user.emailVerified ? (
														<Badge variant="default">
															{t("admin:users.verified")}
														</Badge>
													) : (
														<Badge variant="secondary">
															{t("admin:users.unverified")}
														</Badge>
													)}
												</TableCell>
												<TableCell className="text-muted-foreground text-sm">
													{new Date(user.createdAt).toLocaleDateString()}
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							) : (
								<div className="py-12 text-center">
									<p className="text-muted-foreground">
										{t("admin:users.no_users_found")}
									</p>
								</div>
							)}
						</div>
					</ScrollArea>

					{data && data.total > 0 && (
						<div className="border-t p-4">
							<p className="text-muted-foreground text-sm">
								{t("admin:users.showing_users", {
									count: data.users.length,
									total: data.total,
								})}
							</p>
						</div>
					)}
				</div>

				{/* Sidebar */}
				<div
					className={`w-full border-l bg-muted/20 md:w-72 lg:w-80 ${showDetailsPanelOnMobile ? "block" : "hidden md:block"}`}
				>
					<ScrollArea className="h-full">
						<div className="p-3 md:p-4">
							{selectedUser ? (
								<>
									<div className="mb-4 flex items-start gap-2 md:gap-3">
										<Avatar className="h-16 w-16">
											<AvatarImage src={selectedUser.image || undefined} />
											<AvatarFallback>
												{selectedUser.name.substring(0, 2).toUpperCase()}
											</AvatarFallback>
										</Avatar>
										<div className="min-w-0 flex-1">
											<h3 className="font-semibold text-lg">
												{selectedUser.name}
											</h3>
											<p className="text-muted-foreground text-sm">
												{selectedUser.email}
											</p>
										</div>
										<Button
											variant="ghost"
											size="sm"
											className="h-8 w-8 p-0 md:hidden"
											onClick={() => setShowDetailsPanelOnMobile(false)}
											title="Close"
										>
											<X className="h-4 w-4" />
										</Button>
									</div>

									<Separator />

									<div className="mt-4 space-y-4">
										<div className="flex justify-between text-sm">
											<span className="text-muted-foreground">
												{t("admin:users.quick_info.organizations")}
											</span>
											<span className="font-medium">
												{selectedUser.organizationCount}
											</span>
										</div>

										<div className="flex justify-between text-sm">
											<span className="text-muted-foreground">
												{t("admin:users.quick_info.projects")}
											</span>
											<span className="font-medium">
												{selectedUser.projectCount}
											</span>
										</div>

										<div className="flex flex-col gap-1 text-sm">
											<span className="text-muted-foreground">
												{t("admin:users.quick_info.status")}
											</span>
											<div>
												{selectedUser.emailVerified ? (
													<Badge variant="default">
														{t("admin:users.verified")}
													</Badge>
												) : (
													<Badge variant="secondary">
														{t("admin:users.unverified")}
													</Badge>
												)}
											</div>
										</div>

										<div className="flex flex-col gap-1 text-sm">
											<span className="text-muted-foreground">
												{t("admin:users.quick_info.created")}
											</span>
											<span className="font-medium">
												{new Date(selectedUser.createdAt).toLocaleDateString()}
											</span>
										</div>
									</div>
								</>
							) : (
								<div className="flex h-full flex-col items-center justify-center text-center text-muted-foreground">
									<UserIcon className="mb-4 h-12 w-12" />
									<p className="text-sm">
										{t("admin:users.quick_info.select_hint")}
									</p>
								</div>
							)}
						</div>
					</ScrollArea>
				</div>
			</div>
		</div>
	);
}
